import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'react-toastify';
import { removeLoadingKey } from '../helpers/common';

export const ForecastSettingsLoadingKeys = {
  fetch: 'FETCH_FORECAST_SETTINGS',
  save: 'SAVE_FORECAST_SETTINGS',
};

const defaultSettings = {
  revenue_category_id: 1,
  adSpend_category_id: 3,
  metrics: {
    revenue: null,
    contribution_margin: null,
    net_income: null,
  },
};

const initialState = {
  forecastSettings: defaultSettings,
  loading: false,
  loadingKeys: [],
  error: null,
};

export const fetchForecastSettings = createAsyncThunk(
  'forecastSettings/fetchForecastSettings',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get('/api/v1/metadata/forecast-settings');
      return res.data;
    } catch (error) {
      const message =
        error.response?.data?.error || 'Failed to load forecast settings';
      return rejectWithValue(message);
    }
  }
);

export const saveForecastSettings = createAsyncThunk(
  'forecastSettings/saveForecastSettings',
  async (settings, { rejectWithValue }) => {
    try {
      const res = await axios.post('/api/v1/metadata/forecast-settings', settings);
      toast.success('Forecast settings saved successfully', { position: 'bottom-right' });
      return res.data;
    } catch (error) {
      const message =
        error.response?.data?.error || 'Failed to save forecast settings';
      toast.error(message, { position: 'bottom-right' });
      return rejectWithValue(message);
    }
  }
);

const forecastSettingsSlice = createSlice({
  name: 'forecastSettings',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchForecastSettings.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ForecastSettingsLoadingKeys.fetch];
      })
      .addCase(fetchForecastSettings.fulfilled, (state, action) => {
        state.forecastSettings = action.payload?.settings || defaultSettings;
        state.loadingKeys = removeLoadingKey(ForecastSettingsLoadingKeys.fetch, state.loadingKeys);
        state.loading = state.loadingKeys.length > 0;
      })
      .addCase(fetchForecastSettings.rejected, (state, action) => {
        state.error = action.payload || 'Failed to load forecast settings';
        state.forecastSettings = defaultSettings;
        state.loadingKeys = removeLoadingKey(ForecastSettingsLoadingKeys.fetch, state.loadingKeys);
        state.loading = state.loadingKeys.length > 0;
      })
      .addCase(saveForecastSettings.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ForecastSettingsLoadingKeys.save];
      })
      .addCase(saveForecastSettings.fulfilled, (state, action) => {
        state.forecastSettings = action.payload?.settings || defaultSettings;
        state.loadingKeys = removeLoadingKey(ForecastSettingsLoadingKeys.save, state.loadingKeys);
        state.loading = state.loadingKeys.length > 0;
      })
      .addCase(saveForecastSettings.rejected, (state, action) => {
        state.error = action.payload || 'Failed to save forecast settings';
        state.loadingKeys = removeLoadingKey(ForecastSettingsLoadingKeys.save, state.loadingKeys);
        state.loading = state.loadingKeys.length > 0;
      });
  },
});

export default forecastSettingsSlice.reducer;
